import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

const shoppingGuideLinks = [
  { label: 'Complete Guide', path: '/article/basetao-spreadsheet-guide' },
  { label: 'Basetao Review', path: '/article/basetao-review' },
  { label: 'Shipping Cost', path: '/article/basetao-shipping-cost' },
  { label: 'Coupon & Discount', path: '/article/basetao-coupon' },
  { label: 'Warehouse Guide', path: '/article/basetao-warehouse' },
  { label: 'Is Basetao Legit?', path: '/article/is-basetao-legit' },
  { label: 'Shoes Guide', path: '/article/basetao-spreadsheet-shoes' },
  { label: 'Beginner Tutorial', path: '/article/basetao-spreadsheet-beginners' },
  { label: 'Shipping Methods', path: '/article/basetao-shipping-methods' },
  { label: 'Fees Explained', path: '/article/basetao-fees' },
  { label: 'Alternatives', path: '/article/basetao-alternatives' },
  { label: 'QC Photos Guide', path: '/article/basetao-qc-photos' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleNavClick = () => {
    setMobileOpen(false);
    setMobileGuideOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b-2 border-background-200 shadow-sm">
      <nav className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group flex-shrink-0" onClick={handleNavClick}>
          <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
            <i className="ri-file-list-3-line text-white text-lg"></i>
          </div>
          <span className="font-heading font-bold text-lg text-foreground-900 whitespace-nowrap">
            Basetao Sheet Help
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <Link to="/" className="text-sm font-medium text-foreground-800 hover:text-primary-500 px-3 py-2 rounded-lg hover:bg-background-100 transition-all duration-200 whitespace-nowrap">
            Home
          </Link>

          <div className="relative" ref={guideRef}>
            <button
              onClick={() => setGuideOpen(!guideOpen)}
              className="flex items-center gap-1 text-sm font-medium text-foreground-800 hover:text-primary-500 px-3 py-2 rounded-lg hover:bg-background-100 transition-all duration-200 whitespace-nowrap"
            >
              Shopping Guide
              <div className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${guideOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line text-xs"></i>
              </div>
            </button>
            {guideOpen && (
              <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-background-200 rounded-xl shadow-lg overflow-hidden z-50 py-2">
                {shoppingGuideLinks.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setGuideOpen(false)}
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors duration-150 whitespace-nowrap"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/article/basetao-spreadsheet-guide" className="text-sm font-medium text-foreground-800 hover:text-primary-500 px-3 py-2 rounded-lg hover:bg-background-100 transition-all duration-200 whitespace-nowrap">
            Complete Guide
          </Link>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="text-sm font-semibold bg-primary-500 text-white px-5 py-2.5 rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap ml-2"
          >
            Buy It Now
          </a>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-800 hover:bg-background-100 transition-colors duration-200"
          aria-label="Toggle menu"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </div>
        </button>
      </nav>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-16 z-40 bg-white overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              onClick={handleNavClick}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-800 font-medium hover:bg-background-100 transition-colors duration-150"
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-home-4-line text-lg text-primary-500"></i>
              </div>
              Home
            </Link>

            <div>
              <button
                onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
                className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-foreground-800 font-medium hover:bg-background-100 transition-colors duration-150"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-book-open-line text-lg text-accent-500"></i>
                  </div>
                  Shopping Guide
                </div>
                <div className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileGuideOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line"></i>
                </div>
              </button>
              {mobileGuideOpen && (
                <div className="ml-11 space-y-1 mt-1 border-l-2 border-background-200 pl-4">
                  {shoppingGuideLinks.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={handleNavClick}
                      className="block px-3 py-2.5 text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-150 rounded-lg"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="px-4 py-3">
              <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Categories</span>
            </div>
            <div className="grid grid-cols-2 gap-1 px-4">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={handleNavClick}
                  className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors duration-150"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`ri-${getMobileCatIcon(cat.name)} text-accent-500 text-sm`}></i>
                  </div>
                  {cat.name}
                </a>
              ))}
            </div>

            <div className="px-4 pt-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={handleNavClick}
                className="flex items-center justify-center gap-2 w-full bg-primary-500 text-white font-semibold text-sm py-3 rounded-full hover:bg-primary-600 transition-all duration-300"
              >
                Buy It Now
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </div>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function getMobileCatIcon(name: string): string {
  const lower = name.toLowerCase();
  if (lower.includes('shoe')) return 'footprint-line';
  if (lower.includes('hood')) return 't-shirt-line';
  if (lower.includes('shirt')) return 'shirt-line';
  if (lower.includes('jacket')) return 'hanger-line';
  if (lower.includes('pant') || lower.includes('short')) return 'trousers-line';
  if (lower.includes('head')) return 'user-3-line';
  if (lower.includes('set')) return 'checkbox-multiple-line';
  if (lower.includes('under')) return 'shopping-bag-3-line';
  if (lower.includes('jersey')) return 'team-line';
  if (lower.includes('accessor')) return 'glasses-line';
  if (lower.includes('bag')) return 'handbag-line';
  if (lower.includes('belt')) return 'checkbox-blank-circle-line';
  if (lower.includes('perfume')) return 'vip-crown-line';
  return 'more-line';
}